/**
 * One definition of the Rentmatch score bands for the broker side.
 *
 * The bands follow the Rentmatch calculator: 100 is a perfect match, 80 and up
 * high, 65 and up average, below 65 no match (decision on DEV-3056).
 *
 * This lives in the dashboard and deliberately NOT in the shared package: the
 * broker score and the candidate score are separate calculations by design and
 * must be allowed to diverge. What must never diverge is the banding between
 * the two places that show a broker score, the candidate row and the detail
 * panel. Both had their own copy and drifted twice in a day, once on the input
 * and once on the labels, so the score meant different things depending on
 * where you looked.
 *
 * Colors stay with the components: the row paints a pill (soft background plus
 * dark text) and the panel paints a ring, so they need different values for the
 * same band. Only the band itself and its label are shared here.
 */

export enum ERentMatchBand {
  Perfect = "perfect",
  High = "high",
  Average = "average",
  None = "none",
}

export const RENT_MATCH_BAND_MIN_HIGH = 80;
export const RENT_MATCH_BAND_MIN_AVERAGE = 65;

export const getRentMatchBand = (score: number): ERentMatchBand => {
  if (score === 100) {
    return ERentMatchBand.Perfect;
  }
  if (score >= RENT_MATCH_BAND_MIN_HIGH) {
    return ERentMatchBand.High;
  }
  if (score >= RENT_MATCH_BAND_MIN_AVERAGE) {
    return ERentMatchBand.Average;
  }
  return ERentMatchBand.None;
};

/** Existing keys, present in all three languages, so no new translations. */
const BAND_LABEL_KEYS: Record<ERentMatchBand, string> = {
  [ERentMatchBand.Perfect]: "rentmatch.match.perfect",
  [ERentMatchBand.High]: "rentmatch.match.high",
  [ERentMatchBand.Average]: "rentmatch.match.average",
  [ERentMatchBand.None]: "rentmatch.match.lower",
};

export const getRentMatchBandLabelKey = (score: number): string =>
  BAND_LABEL_KEYS[getRentMatchBand(score)];
